// Blog content utilities and helpers
import { getProducts } from '../services/productService';

/**
 * Transform database product to blog product format
 * @param {Object} product - Product from database
 * @returns {Object} Product formatted for blog display
 */
const transformProductForBlog = (product) => {
  // Get category label from populated subcategory
  const categoryLabel = product.subcategory?.name || 'Product';
  
  // Format price with discount if applicable
  let priceDisplay = `$${product.price}`;
  if (product.discount > 0) {
    const discountedPrice = product.price * (1 - product.discount / 100);
    priceDisplay = `$${discountedPrice.toFixed(2)}`;
  }
  
  // Get badge text from badges array
  let badge = '';
  if (product.badges && product.badges.length > 0) {
    const badgeMap = {
      'editors_choice': "Editor's Choice",
      'best_seller': 'Best Seller',
      'trending': 'Trending',
      'derm_approved': 'Derm Approved'
    };
    badge = badgeMap[product.badges[0]] || '';
  } else if (product.isFeatured) {
    badge = 'Featured';
  }
  
  // Transform key ingredients from object array to string array
  const keyIngredients = product.keyIngredients?.map(ingredient => {
    if (ingredient.benefit) {
      return `${ingredient.name} - ${ingredient.benefit}`;
    }
    return ingredient.name;
  }) || [];
  
  return {
    id: product._id,
    brand: product.brand,
    name: product.name,
    imageUrl: product.imageUrl || '',
    price: priceDisplay,
    rating: product.rating || 4.0,
    reviewCount: product.reviewCount || 0,
    categoryLabel: categoryLabel,
    highlights: product.highlights || product.benefits || [],
    affiliateUrl: product.affiliateLink,
    badge: badge,
    pros: product.pros || [],
    cons: product.cons || [],
    keyIngredients: keyIngredients,
    suitability: product.suitability || [],
    attributes: product.attributes || []
  };
};

/**
 * Get products by category ID from database
 * @param {string} categoryId - Category ID
 * @param {Array} tags - Optional tag filters
 * @param {number} limit - Number of products to return
 * @returns {Promise<Product[]>} Array of products
 */
export const getProductsByCategoryId = async (categoryId, tags = [], limit = 3) => {
  try {
    const queryParams = {
      category: categoryId,
      isActive: true,
      sort: '-rating,-reviewCount',
      limit: limit * 2 // Get more to filter through
    };
    
    // Add tag filtering if provided
    if (tags && tags.length > 0) {
      queryParams.tags = tags;
    }
    
    const response = await getProducts(queryParams);
    const products = response.data || [];
    
    // Prioritize products with badges or featured status
    const sortedProducts = products.sort((a, b) => {
      // Priority 1: Badges
      const aBadges = (a.badges && a.badges.length > 0) ? 1 : 0;
      const bBadges = (b.badges && b.badges.length > 0) ? 1 : 0;
      if (aBadges !== bBadges) return bBadges - aBadges;
      
      // Priority 2: Featured status
      const aFeatured = a.isFeatured ? 1 : 0;
      const bFeatured = b.isFeatured ? 1 : 0;
      if (aFeatured !== bFeatured) return bFeatured - aFeatured;
      
      // Priority 3: Rating
      return (b.rating || 0) - (a.rating || 0);
    });
    
    return sortedProducts.slice(0, limit).map(transformProductForBlog);
  } catch (error) {
    console.error('Error fetching products by category:', error);
    return [];
  }
};

/**
 * Transform content from API to BlogMeta format
 * @param {Object} post - Post data from API
 * @returns {BlogMeta} Formatted meta data
 */
export const transformPostToMeta = (post) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Convert relative image URLs to full URLs for production
  const getCoverImageUrl = (imageUrl) => {
    if (!imageUrl) return undefined;
    // If already a full URL, return as is
    if (imageUrl.startsWith('http')) return imageUrl;
    // If relative URL, use it as is (the backend serves images at /images)
    return imageUrl;
  };

  return {
    eyebrow: post.eyebrow || post.category || 'Blog',
    title: post.title,
    description: post.description || post.excerpt || 'Expert insights and beauty tips from the Radiance team.',
    author: post.author || 'Radiance Team',
    date: formatDate(post.date || post.publishedAt || post.createdAt),
    readTime: post.readTime || '5 min read',
    cover: getCoverImageUrl(post.image),
    faq: post.faq || undefined
  };
};

/**
 * Detect product category slug from post content/tags
 * @param {Object} post - Post data
 * @returns {string|null} Detected category slug or null
 */
export const detectProductCategorySlug = (post) => {
  const content = (post.content || '').toLowerCase();
  const tags = (post.tags || []).join(' ').toLowerCase();
  const combined = `${content} ${tags} ${post.title || ''}`.toLowerCase();

  // More comprehensive keyword matching
  const categoryKeywords = {
    'skincare': ['skincare', 'moisturizer', 'serum', 'retinol', 'cleanser', 'toner', 'sunscreen', 'spf', 'cream', 'lotion', 'facial', 'skin care', 'anti-aging', 'acne', 'wrinkles', 'dark spots', 'hydration', 'exfoliate'],
    'makeup': ['makeup', 'foundation', 'lipstick', 'eyeshadow', 'mascara', 'blush', 'concealer', 'powder', 'bronzer', 'highlighter', 'eyeliner', 'lip gloss', 'cosmetics', 'beauty'],
    'hair-care': ['hair', 'shampoo', 'conditioner', 'hair mask', 'hair treatment', 'hair oil', 'styling', 'hair care', 'haircare', 'scalp', 'curly hair', 'damaged hair', 'hair color'],
    'wellness': ['wellness', 'supplement', 'vitamin', 'body care', 'bath', 'shower', 'sleep', 'relaxation', 'self-care', 'meditation', 'stress relief']
  };

  // Check each category's keywords
  for (const [categorySlug, keywords] of Object.entries(categoryKeywords)) {
    for (const keyword of keywords) {
      if (combined.includes(keyword)) {
        return categorySlug;
      }
    }
  }
  
  return null; // No category detected
};

/**
 * Get smart product recommendations for a blog post
 * This now returns the products directly if they're attached to the post,
 * or fetches them based on detected category
 * @param {Object} post - Post data (with optional featuredProducts)
 * @returns {Array} Array of formatted products (already transformed if from DB)
 */
export const getSmartProductRecommendations = (post) => {
  // If post has manually selected featured products from database, return them
  if (post.featuredProducts && post.featuredProducts.length > 0) {
    // Transform database products to blog format
    return post.featuredProducts.map(transformProductForBlog);
  }
  
  // Otherwise return empty array - BlogPostPage will handle async fetching
  return [];
};

/**
 * Fetch product recommendations asynchronously based on post content
 * @param {Object} post - Post data
 * @param {Array} allCategories - All categories from database
 * @returns {Promise<Array>} Array of formatted products
 */
export const fetchSmartProductRecommendations = async (post, allCategories = []) => {
  // First check if post has manually selected featured products
  if (post.featuredProducts && post.featuredProducts.length > 0) {
    return post.featuredProducts.map(transformProductForBlog);
  }
  
  // Detect category from content
  const detectedSlug = detectProductCategorySlug(post);
  
  if (!detectedSlug) {
    console.log('No category detected for blog post');
    return [];
  }
  
  // Find matching category from database
  const matchedCategory = allCategories.find(cat => 
    cat.slug === detectedSlug || cat.slug === detectedSlug.replace('-', '')
  );
  
  if (!matchedCategory) {
    console.log(`Category not found for slug: ${detectedSlug}`);
    return [];
  }
  
  console.log(`Fetching products for category: ${matchedCategory.name}`);
  
  // Fetch products from database
  const products = await getProductsByCategoryId(matchedCategory._id, [], 3);
  return products;
};

// FAQ templates for common topics
export const FAQ_TEMPLATES = {
  skincare: [
    { q: "Can I use this with other skincare products?", a: "Most skincare products can be layered, but introduce new products gradually and patch test first." },
    { q: "How long before I see results?", a: "Most skincare products show initial results in 2-4 weeks, with full benefits visible after 8-12 weeks of consistent use." },
  ],
  makeup: [
    { q: "What's the best way to apply this product?", a: "Application method can vary by product type. Check the product instructions and consider your skin type." },
    { q: "How do I choose the right shade?", a: "Test shades in natural light when possible, and consider your undertone (warm, cool, or neutral)." },
  ],
  haircare: [
    { q: "How often should I use this product?", a: "Usage frequency depends on your hair type and the specific product. Start with the recommended frequency and adjust as needed." },
    { q: "Is this suitable for colored hair?", a: "Many modern hair products are color-safe, but always check the product label and do a strand test first." },
  ]
};

/**
 * Get relevant FAQs for a post
 * @param {Object} post - Post data
 * @returns {Array} FAQ array
 */
export const getRelevantFAQs = (post) => {
  const categorySlug = detectProductCategorySlug(post);
  
  // Map slug to FAQ template key
  const categoryMap = {
    'skincare': 'skincare',
    'makeup': 'makeup',
    'hair-care': 'haircare',
    'haircare': 'haircare',
    'wellness': 'skincare' // Use skincare FAQs as fallback for wellness
  };
  
  const categoryKey = categoryMap[categorySlug] || 'skincare';
  return FAQ_TEMPLATES[categoryKey] || FAQ_TEMPLATES.skincare;
};
